
import { supabase } from '@/integrations/supabase/client';
import { UserRole } from '@/context/auth/types';

// Forces a token refresh to get updated role claims
export async function refreshTokenForRoleChanges(): Promise<boolean> {
  try {
    console.log('Attempting to refresh token for role updates');
    const { data, error } = await supabase.auth.refreshSession();
    if (error) {
      console.error('Error refreshing token for role updates:', error);
      return false;
    }
    
    const jwt = data.session?.access_token;
    if (jwt) {
      const payload = JSON.parse(atob(jwt.split('.')[1]));
      console.log('JWT refreshed successfully, role claim:', payload?.app_metadata?.role || payload?.role);
      return !!(payload?.app_metadata?.role || payload?.role);
    }
    return false;
  } catch (error) {
    console.error('Failed to refresh token:', error);
    return false;
  }
}

// Gets the role directly from JWT claims - faster than querying database
export async function getRoleFromJWT(): Promise<UserRole | null> {
  try {
    // Use Supabase's SDK to get the current session
    const { data, error } = await supabase.auth.getSession();
    if (error || !data.session) return null;
    
    const jwt = data.session.access_token;
    if (!jwt) return null;
    
    // Decode the JWT payload
    const payload = JSON.parse(atob(jwt.split('.')[1]));
    
    // First check app_metadata.role (primary location)
    const roleFromMeta = payload?.app_metadata?.role;
    if (roleFromMeta) {
      // Convert string role to enum
      switch(roleFromMeta) {
        case 'super_admin': return UserRole.SUPER_ADMIN;
        case 'org_admin': return UserRole.ORG_ADMIN;
        case 'rest_admin': 
        case 'admin':
        case 'owner':
        case 'manager': return UserRole.REST_ADMIN;
        case 'new_sign_up': return UserRole.NEW_SIGN_UP;
        case 'in_progress': return UserRole.IN_PROGRESS;
        case 'user':
        default: return UserRole.USER;
      }
    }
    
    // Fallback to direct role property (though unlikely)
    return payload?.role as UserRole || null;
  } catch (error) {
    console.error('Error extracting role from JWT:', error);
    return null;
  }
}

// For backward compatibility - synchronous version that works with existing code
// This should only be used in places where async/await can't be used
export function getRoleFromJWTSync(): UserRole | null {
  console.debug('JWT-roles: Attempting to extract role from JWT synchronously');
  try {
    // Get the storage key from the Supabase client configuration
    const storageKey = 'supabase.auth.token';
    
    // Try multiple storage approaches to handle different Supabase versions
    
    // Approach 1: Check localStorage with our configured key
    const storedSession = localStorage.getItem(storageKey);
    if (storedSession) {
      try {
        // Parse the stored session
        const session = JSON.parse(storedSession);
        
        // Try different token storage formats
        let accessToken = null;
        
        // Format 1: Old format with currentSession
        if (session.currentSession?.access_token) {
          accessToken = session.currentSession.access_token;
        } 
        // Format 2: Direct access token
        else if (session.access_token) {
          accessToken = session.access_token;
        } 
        // Format 3: Supabase v2 format with session object
        else if (session.session?.access_token) {
          accessToken = session.session.access_token;
        }
        
        if (accessToken) {
          // Decode the token payload
          const payload = JSON.parse(atob(accessToken.split('.')[1]));
          
          // First check app_metadata.role (primary location)
          const roleFromMeta = payload?.app_metadata?.role;
          if (roleFromMeta) {
            console.debug(`JWT-roles: Found role in app_metadata: ${roleFromMeta}`);
            
            // Convert string role to enum
            switch(roleFromMeta) {
              case 'super_admin': return UserRole.SUPER_ADMIN;
              case 'org_admin': return UserRole.ORG_ADMIN;
              case 'rest_admin': 
              case 'admin':
              case 'owner':
              case 'manager': return UserRole.REST_ADMIN;
              case 'user':
              default: return UserRole.USER;
            }
          }
          
          // Fallback to direct role property
          console.debug(`JWT-roles: Found direct role: ${payload?.role}`);
          return payload?.role as UserRole || null;
        }
      } catch (e) {
        console.error('Error parsing token from localStorage:', e);
        // Continue to try other approaches
      }
    }
    
    // Approach 2: Look for sb-xxx-auth-token in localStorage
    // This is how Supabase v2 often stores tokens
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.match(/sb-.*-auth-token/)) {
        try {
          const supabaseData = JSON.parse(localStorage.getItem(key) || '');
          if (supabaseData?.access_token) {
            const payload = JSON.parse(atob(supabaseData.access_token.split('.')[1]));
            
            // First check app_metadata.role (primary location)
            const roleFromMeta = payload?.app_metadata?.role;
            if (roleFromMeta) {
              console.debug(`JWT-roles: Found role in app_metadata using key ${key}: ${roleFromMeta}`);
              
              // Convert string role to enum
              switch(roleFromMeta) {
                case 'super_admin': return UserRole.SUPER_ADMIN;
                case 'org_admin': return UserRole.ORG_ADMIN;
                case 'rest_admin': 
                case 'admin':
                case 'owner':
                case 'manager': return UserRole.REST_ADMIN;
                case 'user':
                default: return UserRole.USER;
              }
            }
            
            // Fallback to direct role property
            console.debug(`JWT-roles: Found direct role using key ${key}: ${payload?.role}`);
            return payload?.role as UserRole || null;
          }
        } catch (e) {
          console.error(`Error parsing potential Supabase token from key ${key}:`, e);
        }
      }
    }
    
    // If all approaches fail, we don't have a valid token
    console.debug('JWT-roles: No valid token found in localStorage');
    return null;
  } catch (error) {
    console.error('Error extracting role from JWT sync:', error);
    return null;
  }
}

// Add comprehensive debugging helper
export function debugAuthState(): void {
  console.group("===== AUTH STATE DEBUG =====");
  
  try {
    // Get session token if available
    const storageKey = 'supabase.auth.token';
    const storedToken = localStorage.getItem(storageKey);
    
    console.log("Local storage auth:", {
      hasToken: !!storedToken,
      tokenLength: storedToken ? storedToken.length : 0
    });
    
    // Check for other Supabase tokens
    const supabaseKeys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.includes('supabase')) {
        supabaseKeys.push(key);
      }
    }
    
    console.log("All Supabase-related localStorage keys:", supabaseKeys);
    
    // Attempt to decode JWT
    if (storedToken) {
      try {
        const session = JSON.parse(storedToken);
        let token = null;
        
        if (session.currentSession?.access_token) {
          token = session.currentSession.access_token;
        } else if (session.access_token) {
          token = session.access_token;
        } else if (session.session?.access_token) {
          token = session.session.access_token;
        }
        
        if (token) {
          const payload = JSON.parse(atob(token.split('.')[1]));
          console.log("JWT Payload Structure:", {
            sub: payload.sub,
            roleDirectly: payload.role,
            roleInAppMetadata: payload.app_metadata?.role,
            hasAppMetadata: !!payload.app_metadata,
            appMetadataKeys: payload.app_metadata ? Object.keys(payload.app_metadata) : [],
            expiresAt: payload.exp ? new Date(payload.exp * 1000).toISOString() : null
          });
        }
      } catch (e) {
        console.error("Error parsing JWT:", e);
      }
    }
  } catch (e) {
    console.error("Debug auth state error:", e);
  }
  
  console.groupEnd();
}
